import React, { useState, useEffect, useMemo } from "react";
import axios from "../common/axios";
import DashboardShell from "../layout/dashboard/DashboardShell";
import { Circles } from "react-loader-spinner";
import { DataGridPremium, useGridApiRef } from "@mui/x-data-grid-premium";
import dayjs from "dayjs";
import useDocumentTitle from "../common/useDocumentTitle";
import { useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { SearchableCombobox } from "../components/SearchableCombobox";
import { cn } from "../lib/utils";

// 날짜 문자열을 dayjs 객체로 변환. null/빈값이면 null 반환
function parseDate(val) {
  if (!val) return null;
  const d = dayjs(val);
  return d.isValid() ? d : null;
}

// baseDate 기준 각 집계 조건 함수들
function count5day(records, base) {
  return records.filter((v) => {
    const d = parseDate(v.SubmissionPlanDate);
    if (!d) return false;
    return !d.isBefore(base, "day") && !d.isAfter(base.add(3, "day"), "day");
  }).length;
}

function count1week(records, base) {
  return records.filter((v) => {
    const d = parseDate(v.SubmissionPlanDate);
    if (!d) return false;
    return !d.isBefore(base, "day") && !d.isAfter(base.add(7, "day"), "day");
  }).length;
}

function count2week(records, base) {
  return records.filter((v) => {
    const d = parseDate(v.SubmissionPlanDate);
    if (!d) return false;
    return !d.isBefore(base, "day") && !d.isAfter(base.add(14, "day"), "day");
  }).length;
}

function countSubmissionPlan(records, base) {
  return records.filter((v) => {
    const d = parseDate(v.SubmissionPlanDate);
    if (!d) return false;
    return !d.isAfter(base, "day");
  }).length;
}

function countSubmissionActual(records, base) {
  return records.filter((v) => {
    const d = parseDate(v.SubmissionActualDate);
    if (!d) return false;
    return !d.isAfter(base, "day");
  }).length;
}

function countApprovalPlan(records, base) {
  return records.filter((v) => {
    const d = parseDate(v.ApprovalPlanDate);
    if (!d) return false;
    return !d.isAfter(base, "day");
  }).length;
}

function countApprovalActual(records, base) {
  return records.filter((v) => {
    const d = parseDate(v.ApprovalActualDate);
    if (!d) return false;
    return !d.isAfter(base, "day");
  }).length;
}

// 사전검토 계획일이 기준일 이전인데 아직 실적일이 없는 건수(지연)
function countSubmissionDelayed(records, base) {
  return records.filter((v) => {
    const planDate = parseDate(v.SubmissionPlanDate);
    if (!planDate || planDate.isAfter(base, "day")) return false;
    return !parseDate(v.SubmissionActualDate);
  }).length;
}

// 해당 셀 클릭 시 보여줄 레코드 필터 함수
function getFilteredRecords(records, columnKey, base) {
  switch (columnKey) {
    case "5day":
      return records.filter((v) => {
        const d = parseDate(v.SubmissionPlanDate);
        if (!d) return false;
        return !d.isBefore(base, "day") && !d.isAfter(base.add(3, "day"), "day");
      });
    case "1week":
      return records.filter((v) => {
        const d = parseDate(v.SubmissionPlanDate);
        if (!d) return false;
        return !d.isBefore(base, "day") && !d.isAfter(base.add(7, "day"), "day");
      });
    case "2week":
      return records.filter((v) => {
        const d = parseDate(v.SubmissionPlanDate);
        if (!d) return false;
        return !d.isBefore(base, "day") && !d.isAfter(base.add(14, "day"), "day");
      });
    case "subPlan":
      return records.filter((v) => {
        const d = parseDate(v.SubmissionPlanDate);
        if (!d) return false;
        return !d.isAfter(base, "day");
      });
    case "subActual":
      return records.filter((v) => {
        const d = parseDate(v.SubmissionActualDate);
        if (!d) return false;
        return !d.isAfter(base, "day");
      });
    case "appPlan":
      return records.filter((v) => {
        const d = parseDate(v.ApprovalPlanDate);
        if (!d) return false;
        return !d.isAfter(base, "day");
      });
    case "appActual":
      return records.filter((v) => {
        const d = parseDate(v.ApprovalActualDate);
        if (!d) return false;
        return !d.isAfter(base, "day");
      });
    default:
      return [];
  }
}

// Dialog 내부 DataGridPremium 컬럼 정의
const detailColumns = [
  { field: "record_no", headerName: "레코드번호", width: 130 },
  { field: "DrawingDiscPD", headerName: "공종", width: 80 },
  { field: "VendorsDPK", headerName: "업체", width: 160 },
  { field: "DrawingNo", headerName: "도면번호", width: 160 },
  { field: "DrawingName", headerName: "도면명", width: 200 },
  {
    field: "SubmissionPlanDate",
    headerName: "사전검토 계획일",
    width: 140,
    valueFormatter: (value) => (value ? dayjs(value).format("YYYY-MM-DD") : ""),
  },
  {
    field: "SubmissionActualDate",
    headerName: "사전검토 실적일",
    width: 140,
    valueFormatter: (value) => (value ? dayjs(value).format("YYYY-MM-DD") : ""),
  },
  {
    field: "ApprovalPlanDate",
    headerName: "CPMS 계획일",
    width: 130,
    valueFormatter: (value) => (value ? dayjs(value).format("YYYY-MM-DD") : ""),
  },
  {
    field: "ApprovalActualDate",
    headerName: "CPMS 실적일",
    width: 130,
    valueFormatter: (value) => (value ? dayjs(value).format("YYYY-MM-DD") : ""),
  },
];


const Dashboard = () => {
  useDocumentTitle("SHI DP 대시보드");
  const location = useLocation();

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const [projectCodeList, setProjectCodeList] = useState([]);
  const [projectCode, setProjectCode] = useState({
    projectnumber: "",
    projectname: "",
  });
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [baseDate, setBaseDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [dpByProject, setDpByProject] = useState([]);

  const dialogGridApiRef = useGridApiRef();

  // 상세 팝업 상태
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogRows, setDialogRows] = useState([]);

  // localStorage에서 프로젝트 복원
  useEffect(() => {
    const projectnumber = localStorage.getItem("projectnumber");
    const projectname = localStorage.getItem("projectname");
    if (projectnumber && projectname) {
      setProjectCode({ projectnumber, projectname });
    }
  }, []);

  // 프로젝트 선택 시 localStorage 저장
  useEffect(() => {
    if (projectCode && projectCode.projectname !== "") {
      localStorage.setItem("projectnumber", projectCode.projectnumber);
      localStorage.setItem("projectname", projectCode.projectname);
    }
  }, [projectCode]);

  // 프로젝트 목록 조회
  useEffect(() => {
    const fetchData = async () => {
      const res = await axios.get(`/getprojectlist`);
      if (res.data.success) {
        setProjectCodeList(res.data.data);
      }
    };
    fetchData();
  }, [location]);

  // 프로젝트별 DP 진척도 조회
  useEffect(() => {
    const fetchDpProgress = async () => {
      try {
        const res = await axios.get("/getProjectDpProgress");
        if (res.data.success) {
          setDpByProject(
            res.data.data.map((d) => ({
              name: d.projectname,
              actual: d.actCount,
              total: d.planCount,
              planByToday: d.planByTodayCount ?? 0,
            }))
          );
        }
      } catch {}
    };
    fetchDpProgress();
  }, []);

  // 승인도 데이터 조회 - discipline 필터 없이 전체 조회
  useEffect(() => {
    const fetchData = async () => {
      if (!projectCode || !projectCode.projectnumber) return;
      setLoading(true);
      try {
        const res = await axios.get(`/getdpAdata?path=${projectCode.projectnumber}`);
        if (res.data.success) {
          setRawData(res.data.data.data);
        }
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [projectCode]);

  // (공종, 업체) groupBy 집계
  const tableRows = useMemo(() => {
    if (!rawData.length) return [];
    const base = dayjs(baseDate);

    // 그룹핑
    const groupMap = new Map();
    rawData.forEach((v) => {
      const key = `${v.DrawingDiscPD}__${v.VendorsDPK}`;
      if (!groupMap.has(key)) {
        groupMap.set(key, { disc: v.DrawingDiscPD, vendor: v.VendorsDPK, records: [] });
      }
      groupMap.get(key).records.push(v);
    });

    const rows = Array.from(groupMap.values()).map((g) => ({
      disc: g.disc,
      vendor: g.vendor,
      total: g.records.length,
      c5day: count5day(g.records, base),
      c1week: count1week(g.records, base),
      c2week: count2week(g.records, base),
      subPlan: countSubmissionPlan(g.records, base),
      subActual: countSubmissionActual(g.records, base),
      appPlan: countApprovalPlan(g.records, base),
      appActual: countApprovalActual(g.records, base),
      records: g.records,
    }));

    // 공종별 소계 행 삽입
    const discMap = new Map();
    rows.forEach((r) => {
      if (!discMap.has(r.disc)) discMap.set(r.disc, []);
      discMap.get(r.disc).push(r);
    });

    const result = [];
    discMap.forEach((discRows, disc) => {
      discRows.forEach((r) => result.push(r));
      const discRecords = discRows.flatMap((r) => r.records);
      result.push({
        disc,
        vendor: "소계",
        total: discRows.reduce((s, r) => s + r.records.length, 0),
        c5day: discRows.reduce((s, r) => s + r.c5day, 0),
        c1week: discRows.reduce((s, r) => s + r.c1week, 0),
        c2week: discRows.reduce((s, r) => s + r.c2week, 0),
        subPlan: discRows.reduce((s, r) => s + r.subPlan, 0),
        subActual: discRows.reduce((s, r) => s + r.subActual, 0),
        appPlan: discRows.reduce((s, r) => s + r.appPlan, 0),
        appActual: discRows.reduce((s, r) => s + r.appActual, 0),
        records: discRecords,
        isSubtotal: true,
      });
    });

    // TOTAL 행
    const total = {
      disc: "설계 전체",
      vendor: "TOTAL",
      total: rawData.length,
      c5day: rows.reduce((s, r) => s + r.c5day, 0),
      c1week: rows.reduce((s, r) => s + r.c1week, 0),
      c2week: rows.reduce((s, r) => s + r.c2week, 0),
      subPlan: rows.reduce((s, r) => s + r.subPlan, 0),
      subActual: rows.reduce((s, r) => s + r.subActual, 0),
      appPlan: rows.reduce((s, r) => s + r.appPlan, 0),
      appActual: rows.reduce((s, r) => s + r.appActual, 0),
      records: rawData,
      isTotal: true,
    };

    return [...result, total];
  }, [rawData, baseDate]);

  // KPI 카드용 요약 지표 - TOTAL 행(필터 미적용) 기준
  const kpiSummary = useMemo(() => {
    const total = tableRows.find((r) => r.isTotal);
    if (!total || total.total === 0) {
      return { totalCount: 0, appPct: 0, subPct: 0, dueIn1Week: 0, delayed: 0 };
    }
    return {
      totalCount: total.total,
      appPct: (total.appActual / total.total) * 100,
      subPct: (total.subActual / total.total) * 100,
      dueIn1Week: total.c1week,
      delayed: countSubmissionDelayed(rawData, dayjs(baseDate)),
    };
  }, [tableRows, rawData, baseDate]);

  const [filterDisc, setFilterDisc] = useState("");
  const [filterVendor, setFilterVendor] = useState("");

  const projectOptions = useMemo(
    () => projectCodeList.map((p) => ({ value: p.projectnumber, label: p.projectname || "" })),
    [projectCodeList]
  );
  const discOptions = useMemo(
    () =>
      [...new Set(rawData.map((v) => v.DrawingDiscPD).filter(Boolean))].map((v) => ({
        value: v,
        label: v,
      })),
    [rawData]
  );
  const vendorOptions = useMemo(
    () =>
      [...new Set(rawData.map((v) => v.VendorsDPK).filter(Boolean))].map((v) => ({
        value: v,
        label: v,
      })),
    [rawData]
  );

  const filteredTableRows = useMemo(() => {
    if (!filterDisc && !filterVendor) return tableRows;
    return tableRows.filter((row) => {
      if (row.isTotal) return true;
      if (row.isSubtotal) {
        if (filterDisc && row.disc !== filterDisc) return false;
        if (filterVendor) return false;
        return true;
      }
      if (filterDisc && row.disc !== filterDisc) return false;
      if (filterVendor && row.vendor !== filterVendor) return false;
      return true;
    });
  }, [tableRows, filterDisc, filterVendor]);

  // 셀 클릭 핸들러
  const handleCellClick = (row, colKey, colLabel) => {
    const base = dayjs(baseDate);
    const filtered = getFilteredRecords(row.records, colKey, base);
    const label = `${row.disc} / ${row.vendor} - ${colLabel}`;
    setDialogTitle(label);
    // DataGridPremium용 id 부여
    setDialogRows(filtered.map((r, i) => ({ ...r, id: i })));
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  // 집계 셀 렌더
  const Cell = ({ row, colKey, value, colLabel }) => (
    <td
      className="border border-border px-2.5 py-1.5 text-center cursor-pointer hover:bg-blue-200"
      onClick={() => handleCellClick(row, colKey, colLabel)}
    >
      {value}
    </td>
  );

  return (
    <DashboardShell pageTitle="대시보드">
      <div className="flex-1 flex flex-col gap-4 p-4 min-h-0">
        {/* 페이지 헤더 */}
        <div>
          <h1 className="text-xl font-bold text-foreground">
            {(projectCode && projectCode.projectname) || "DP 대시보드"}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            기준일 {baseDate} · 공종/업체별 승인도 진행 현황을 확인합니다.
          </p>
        </div>

        {/* KPI 카드 (공종/업체 필터 미적용 - 선택 프로젝트 전체 기준) */}
        <p className="text-xs text-muted-foreground -mb-1">
          아래 지표는 선택한 프로젝트 전체 기준이며, 공종/업체 필터는 반영되지 않습니다.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <Card className="relative overflow-hidden">
            <span className="absolute inset-x-0 top-0 h-1 bg-blue-600" />
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground">전체 도면 수</div>
              <div className="text-2xl font-bold text-foreground mt-1">
                {kpiSummary.totalCount.toLocaleString()}
                <span className="text-sm font-normal text-muted-foreground ml-1">건</span>
              </div>
            </CardContent>
          </Card>
          <Card className="relative overflow-hidden">
            <span className="absolute inset-x-0 top-0 h-1 bg-emerald-500" />
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground">CPMS 진척율</div>
              <div className="text-2xl font-bold text-foreground mt-1">
                {kpiSummary.appPct.toFixed(1)}
                <span className="text-sm font-normal text-muted-foreground ml-1">%</span>
              </div>
            </CardContent>
          </Card>
          <Card className="relative overflow-hidden">
            <span className="absolute inset-x-0 top-0 h-1 bg-sky-400" />
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground">사전검토 진척율</div>
              <div className="text-2xl font-bold text-foreground mt-1">
                {kpiSummary.subPct.toFixed(1)}
                <span className="text-sm font-normal text-muted-foreground ml-1">%</span>
              </div>
            </CardContent>
          </Card>
          <Card className="relative overflow-hidden">
            <span className="absolute inset-x-0 top-0 h-1 bg-amber-500" />
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground">1주 이내 마감</div>
              <div className="text-2xl font-bold text-foreground mt-1">
                {kpiSummary.dueIn1Week}
                <span className="text-sm font-normal text-muted-foreground ml-1">건</span>
              </div>
            </CardContent>
          </Card>
          <Card className="relative overflow-hidden">
            <span className="absolute inset-x-0 top-0 h-1 bg-red-500" />
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground">계획 대비 지연</div>
              <div className="text-2xl font-bold text-foreground mt-1">
                {kpiSummary.delayed}
                <span className="text-sm font-normal text-muted-foreground ml-1">건</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* 프로젝트별 DP 진척도 */}
        {dpByProject.filter((dp) => dp.total > 0).length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">프로젝트별 DP 진척도</CardTitle>
              <p className="text-xs text-muted-foreground">* 사전검토일 기준</p>
            </CardHeader>
            <CardContent className="flex flex-col gap-3 pt-0">
              {dpByProject
                .filter((dp) => dp.total > 0)
                .map((dp) => {
                  const actPct = Math.min((dp.actual / dp.total) * 100, 100);
                  const planPct = Math.min((dp.planByToday / dp.total) * 100, 100);
                  return (
                    <div key={dp.name}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-medium text-foreground">{dp.name}</span>
                        <span className="text-muted-foreground">
                          {actPct.toFixed(1)}% ({planPct.toFixed(1)}%) / {dp.actual}건 ({dp.planByToday}건) · 총{" "}
                          {dp.total}건
                        </span>
                      </div>
                      <div className="relative h-2 rounded bg-muted">
                        <div
                          className="h-full rounded bg-emerald-500"
                          style={{ width: `${actPct}%` }}
                        />
                        <span
                          className="absolute top-1/2 h-3 w-0.5 -translate-y-1/2 bg-foreground/70"
                          style={{ left: `${planPct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
            </CardContent>
          </Card>
        )}

        {/* 필터 툴바 */}
        <Card>
          <CardContent className="p-3 flex flex-wrap items-end gap-2">
            <SearchableCombobox
              label="Project"
              value={projectCode?.projectnumber || ""}
              onChange={(newValue) => {
                const found = projectCodeList.find((p) => p.projectnumber === newValue);
                setProjectCode(found || { projectnumber: "", projectname: "" });
              }}
              options={projectOptions}
              placeholder="프로젝트 선택"
              width={260}
            />
            <SearchableCombobox
              label="공종"
              value={filterDisc}
              onChange={setFilterDisc}
              options={discOptions}
              placeholder="전체"
              allLabel="전체"
              width={140}
            />
            <SearchableCombobox
              label="업체"
              value={filterVendor}
              onChange={setFilterVendor}
              options={vendorOptions}
              placeholder="전체"
              allLabel="전체"
              width={180}
            />
            <div className="flex flex-col gap-1" style={{ width: 160 }}>
              <Label className="text-xs text-muted-foreground">기준 날짜</Label>
              <Input
                type="date"
                value={baseDate}
                onChange={(e) => setBaseDate(e.target.value)}
                className="h-9"
              />
            </div>
          </CardContent>
        </Card>

        {/* 집계 테이블 */}
        <Card className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <CardContent className="p-0 flex-1 min-h-0">
            {loading ? (
              <div className="loadingContainer relative flex items-center justify-center py-16">
                <Circles ariaLabel="loading" color="#00BFFF" height={100} width={100} />
              </div>
            ) : (
              <div className="overflow-auto h-full">
                <table className="w-full border-collapse text-[13px]">
                  <thead className="sticky top-0 z-10">
                    {/* 헤더 1행 */}
                    <tr>
                      <th rowSpan={3} className={thClass}>
                        공종
                      </th>
                      <th rowSpan={3} className={thClass}>
                        업체
                      </th>
                      <th rowSpan={3} className={thClass}>
                        총건수
                      </th>
                      <th colSpan={3} className={thClass}>
                        제출 계획
                      </th>
                      <th colSpan={5} className={thClass}>
                        계획 대비 제출 실적
                      </th>
                      <th rowSpan={3} className={thClass}>
                        CPMS
                        <br />
                        진척율%
                      </th>
                    </tr>
                    {/* 헤더 2행 */}
                    <tr>
                      <th rowSpan={2} className={thClass}>
                        3일내
                        <br />
                        계획
                      </th>
                      <th rowSpan={2} className={thClass}>
                        1주
                        <br />
                        계획
                      </th>
                      <th rowSpan={2} className={thClass}>
                        2주
                        <br />
                        계획
                      </th>
                      <th colSpan={2} className={thClass}>
                        사전검토
                      </th>
                      <th rowSpan={2} className={thClass}>
                        사전검토
                        <br />
                        진척율%
                      </th>
                      <th colSpan={2} className={thClass}>
                        CPMS
                      </th>
                    </tr>
                    {/* 헤더 3행 */}
                    <tr>
                      <th className={thClass}>계획</th>
                      <th className={thClass}>실적</th>
                      <th className={thClass}>계획</th>
                      <th className={thClass}>실적</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.length === 0 ? (
                      <tr>
                        <td colSpan={12} className="border border-border px-2.5 py-5 text-center">
                          프로젝트를 선택하세요
                        </td>
                      </tr>
                    ) : (
                      filteredTableRows
                        .filter((r) => !r.isTotal)
                        .map((row, idx) => {
                          const isSubtotal = row.isSubtotal;
                          const rowBg = isSubtotal
                            ? "bg-blue-100/60 font-bold"
                            : idx % 2 === 0
                              ? "bg-background"
                              : "bg-muted/40";

                          return (
                            <tr key={`${row.disc}-${row.vendor}`} className={rowBg}>
                              <td className={cn(tdClass, "text-left", isSubtotal && "font-bold")}>
                                {row.disc}
                              </td>
                              <td className={cn(tdClass, "text-left", isSubtotal && "font-bold")}>
                                {row.vendor}
                              </td>
                              <td className={cn(tdClass, "text-center")}>{row.total}</td>
                              <Cell row={row} colKey="5day" value={row.c5day} colLabel="3일내 계획" />
                              <Cell row={row} colKey="1week" value={row.c1week} colLabel="1주 계획" />
                              <Cell row={row} colKey="2week" value={row.c2week} colLabel="2주 계획" />
                              <Cell row={row} colKey="subPlan" value={row.subPlan} colLabel="사전검토 계획" />
                              <Cell row={row} colKey="subActual" value={row.subActual} colLabel="사전검토 실적" />
                              <td className={cn(tdClass, "text-center")}>
                                {row.total === 0 ? "-" : (row.subActual / row.total * 100).toFixed(1) + "%"}
                              </td>
                              <Cell row={row} colKey="appPlan" value={row.appPlan} colLabel="CPMS 계획" />
                              <Cell row={row} colKey="appActual" value={row.appActual} colLabel="CPMS 실적" />
                              <td className={cn(tdClass, "text-center")}>
                                {row.total === 0 ? "-" : (row.appActual / row.total * 100).toFixed(1) + "%"}
                              </td>
                            </tr>
                          );
                        })
                    )}
                  </tbody>
                  {tableRows.some((r) => r.isTotal) &&
                    (() => {
                      const total = tableRows.find((r) => r.isTotal);
                      return (
                        <tfoot className="sticky bottom-0 z-10">
                          <tr className="bg-blue-100 font-bold">
                            <td className={cn(tdClass, "text-left font-bold")}>{total.disc}</td>
                            <td className={cn(tdClass, "text-left font-bold")}>{total.vendor}</td>
                            <td className={cn(tdClass, "text-center")}>{total.total}</td>
                            <Cell row={total} colKey="5day" value={total.c5day} colLabel="3일내 계획" />
                            <Cell row={total} colKey="1week" value={total.c1week} colLabel="1주 계획" />
                            <Cell row={total} colKey="2week" value={total.c2week} colLabel="2주 계획" />
                            <Cell row={total} colKey="subPlan" value={total.subPlan} colLabel="사전검토 계획" />
                            <Cell row={total} colKey="subActual" value={total.subActual} colLabel="사전검토 실적" />
                            <td className={cn(tdClass, "text-center")}>
                              {total.total === 0 ? "-" : (total.subActual / total.total * 100).toFixed(1) + "%"}
                            </td>
                            <Cell row={total} colKey="appPlan" value={total.appPlan} colLabel="CPMS 계획" />
                            <Cell row={total} colKey="appActual" value={total.appActual} colLabel="CPMS 실적" />
                            <td className={cn(tdClass, "text-center")}>
                              {total.total === 0 ? "-" : (total.appActual / total.total * 100).toFixed(1) + "%"}
                            </td>
                          </tr>
                        </tfoot>
                      );
                    })()}
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* 상세 팝업 Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-6xl w-[90vw] h-[80vh] max-h-[calc(100vh-2rem)] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-auto">
            <DataGridPremium
              apiRef={dialogGridApiRef}
              rows={dialogRows}
              columns={detailColumns}
              density="compact"
              disableRowSelectionOnClick
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              pagination
            />
          </div>
          <DialogFooter>
            <Button
              onClick={() =>
                dialogGridApiRef.current.exportDataAsExcel({
                  fileName: dialogTitle,
                })
              }
              variant="outline"
              className="text-emerald-600 border-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"
            >
              엑셀 다운로드
            </Button>
            <Button onClick={handleDialogClose} variant="default">
              닫기
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
};

// 공통 헤더/데이터 셀 스타일
const thClass = "bg-[#1e3a5f] text-white px-2.5 py-2 border border-border text-center whitespace-nowrap";
const tdClass = "px-2.5 py-1.5 border border-border";

export default Dashboard;
